import { supabase } from "../SupabaseClient.tsx";

export const DEFAULT_BENEFICIARY = "Myself";

export interface BeneficiarySettings {
  beneficiaries: string[];
  accountHolderName: string | null;
}

// Find the saved beneficiary matching a name (case/whitespace-insensitive)
export function matchBeneficiary(
  name: string | null | undefined,
  beneficiaries: string[]
): string | null {
  const normalized = name?.trim().toLowerCase();
  if (!normalized) return null;
  return (
    beneficiaries.find((b) => b.trim().toLowerCase() === normalized) ?? null
  );
}

// The first beneficiary in the list is the default for new receipts
export function getDefaultBeneficiary(beneficiaries: string[]): string {
  return beneficiaries[0] || DEFAULT_BENEFICIARY;
}

// Fetch the user's beneficiary list and name (used to help the AI match receipts)
export async function fetchBeneficiarySettings(): Promise<BeneficiarySettings> {
  try {
    const {
      data: { user },
    } = await supabase.auth.getUser();
    const accountHolderName =
      user?.user_metadata?.full_name || user?.user_metadata?.name || null;

    const { data, error } = await supabase
      .from("users")
      .select("beneficiaries")
      .single();

    if (error) {
      console.error("Error fetching beneficiaries:", error);
    }

    const beneficiaries: string[] = data?.beneficiaries?.length
      ? data.beneficiaries
      : [DEFAULT_BENEFICIARY];

    return { beneficiaries, accountHolderName };
  } catch (error) {
    console.error("Unexpected error fetching beneficiaries:", error);
    return { beneficiaries: [DEFAULT_BENEFICIARY], accountHolderName: null };
  }
}

// Add any names that aren't already in the user's beneficiary list.
// Returns the updated list, or null if nothing changed.
export async function addMissingBeneficiaries(
  names: (string | null | undefined)[]
): Promise<string[] | null> {
  const {
    data: { user },
  } = await supabase.auth.getUser();
  if (!user) return null;

  const { data, error } = await supabase
    .from("users")
    .select("beneficiaries")
    .eq("user_id", user.id)
    .single();

  if (error) {
    console.error("Error fetching beneficiaries:", error);
    return null;
  }

  const existing: string[] = data?.beneficiaries?.length
    ? data.beneficiaries
    : [DEFAULT_BENEFICIARY];
  const updated = [...existing];

  for (const name of names) {
    const trimmed = name?.trim();
    if (trimmed && !matchBeneficiary(trimmed, updated)) {
      updated.push(trimmed);
    }
  }

  if (updated.length === existing.length) return null;

  const { error: updateError } = await supabase
    .from("users")
    .update({ beneficiaries: updated })
    .eq("user_id", user.id);

  if (updateError) {
    console.error("Error adding beneficiaries:", updateError);
    return null;
  }

  return updated;
}
